/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { useProjectSiteParams } from '@/shared/lib';
import type { MapProviderProps } from '../../model/map-provider.interface';

interface MapStateManagerProps {
  locations: MapProviderProps['locations'];
  initialFit?: boolean;
}

export function MapStateManager({ locations, initialFit = true }: MapStateManagerProps) {
  const map = useMap();
  const hasInitialFit = useRef(false);
  const lastFitLocations = useRef<typeof locations>([]);
  const isUserInteracting = useRef(false);
  const { projectId, siteId } = useProjectSiteParams();
  const lastProjectSite = useRef({ projectId, siteId });

  // Track user interactions
  useMapEvents({
    movestart: (e: any) => {
      // Check if move was triggered by user
      if (e.originalEvent) {
        isUserInteracting.current = true;
      }
    },
    zoomstart: (e: any) => {
      // Check if zoom was triggered by user
      if (e.originalEvent) {
        isUserInteracting.current = true;
      }
    }
  });

  // Reset state when project/site changes
  useEffect(() => {
    if (lastProjectSite.current.projectId !== projectId || lastProjectSite.current.siteId !== siteId) {
      hasInitialFit.current = false;
      isUserInteracting.current = false;
      lastProjectSite.current = { projectId, siteId };
    }
  }, [projectId, siteId]);

  // Fit bounds on initial load or when project/site changes
  useEffect(() => {
    if (!initialFit || isUserInteracting.current) return;
    if (locations.length === 0) return;
    if (lastFitLocations.current === locations) return;

    const bounds = locations.map((loc) => [loc.lat, loc.lng] as [number, number]);

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
      hasInitialFit.current = true;
      lastFitLocations.current = locations;
    }
  }, [initialFit, locations, map, projectId, siteId]); // Add projectId and siteId to dependencies

  return null;
}
